import React, { useState, useMemo } from 'react';

import { useEmailValidation } from '../hooks/useEmailValidation';
import { useForgotPassword } from '../hooks/useForgotPassword';
import { getInitialEmailMode } from '../services/authRouteMode';

import { EmailSectionRenderer } from './EmailSectionRenderer';

type CreateSubmitHandler = (
  step: 'email' | 'password',
  mode: 'login' | 'forgot' | 'register',
  email: string,
  password: string,
  displayName: string,
  country: string,
  stateCode: string,
  validateEmail: (email: string) => boolean,
  validatePassword: (password: string) => boolean,
  setStep: (step: 'email' | 'password') => void,
  onSubmit: (email: string, password?: string, displayName?: string, country?: string, stateCode?: string) => void
) => (e?: React.FormEvent) => void;

type CreateBackToEmailHandler = (
  setStep: (step: 'email' | 'password') => void,
  setPassword: (password: string) => void,
  setLocalError: (error: string | null) => void,
  onErrorClear?: () => void
) => () => void;

type CreateBackToLoginHandler = (
  setMode: (mode: 'login' | 'forgot') => void,
  setForgotSuccess: (success: boolean) => void,
  setLocalError: (error: string | null) => void,
  setForgotError: (error: string | null) => void,
  onErrorClear?: () => void
) => () => void;

type CreateSwitchModeHandler = (
  setMode: (mode: 'login' | 'forgot' | 'register') => void,
  setLocalError: (error: string | null) => void,
  onErrorClear?: () => void
) => (newMode: 'login' | 'register') => void;

interface EmailSectionContainerWrapperProps {
  onSubmit: (email: string, password?: string, displayName?: string, country?: string, stateCode?: string) => void;
  isLoading?: boolean;
  externalError?: string;
  onErrorClear?: () => void;
  createSubmitHandler: CreateSubmitHandler;
  createBackToEmailHandler: CreateBackToEmailHandler;
  createBackToLoginHandler: CreateBackToLoginHandler;
  createSwitchModeHandler: CreateSwitchModeHandler;
}

const useBasicFormFields = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [country, setCountry] = useState('');
  const [stateCode, setStateCode] = useState('');

  return {
    email, setEmail, password, setPassword, displayName, setDisplayName,
    country, setCountry, stateCode, setStateCode,
  };
};

const useFormState = () => {
  const fields = useBasicFormFields();
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [mode, setMode] = useState<'login' | 'forgot' | 'register'>(() => (
    getInitialEmailMode(window.location.pathname)
  ));

  return useMemo(() => ({
    ...fields,
    step, setStep, mode, setMode,
  }), [fields, step, mode]);
};

const useHandlers = (
  props: EmailSectionContainerWrapperProps,
  form: ReturnType<typeof useFormState>,
  validation: ReturnType<typeof useEmailValidation>,
  forgot: ReturnType<typeof useForgotPassword>
) => ({
  handleSubmit: props.createSubmitHandler(
    form.step, form.mode, form.email, form.password, form.displayName, 
    form.country, form.stateCode, validation.validateEmail, validation.validatePassword, 
    form.setStep, props.onSubmit
  ),
  handleBackToEmail: props.createBackToEmailHandler(
    form.setStep, form.setPassword, validation.setLocalError, props.onErrorClear
  ),
  handleBackToLogin: props.createBackToLoginHandler(
    form.setMode, forgot.setForgotSuccess, validation.setLocalError, forgot.setForgotError, props.onErrorClear
  ),
  handleSwitchMode: props.createSwitchModeHandler(
    form.setMode, validation.setLocalError, props.onErrorClear
  ),
});

export const EmailSectionContainerWrapper: React.FC<EmailSectionContainerWrapperProps> = (props) => {
  const form = useFormState();
  const validation = useEmailValidation(props.onErrorClear);
  const forgot = useForgotPassword(form.email, props.onErrorClear);
  const handlers = useHandlers(props, form, validation, forgot);

  return (
    <EmailSectionRenderer
      forgotSuccess={forgot.forgotSuccess} mode={form.mode} email={form.email} password={form.password}
      displayName={form.displayName} country={form.country} stateCode={form.stateCode}
      step={form.step} error={props.externalError || validation.localError || forgot.forgotError}
      isLoading={props.isLoading ?? false} forgotLoading={forgot.forgotLoading} setEmail={form.setEmail}
      setPassword={form.setPassword} setDisplayName={form.setDisplayName} setCountry={form.setCountry}
      setStateCode={form.setStateCode}
      handleSubmit={handlers.handleSubmit}
      handleBackToEmail={handlers.handleBackToEmail} handleBackToLogin={handlers.handleBackToLogin}
      handleForgotPassword={forgot.handleForgotPassword} setMode={form.setMode}
      handleSwitchMode={handlers.handleSwitchMode}
    />
  );
};
